<template>
  <div class="cr-table-scroll">
    <table class="table table-hover batches-table">
      <thead>
      <tr>
        <th>Nº lote</th>
        <th>Tipo</th>
        <th>Fechado em</th>
        <th class="hide-sm">Enviado em</th>
        <th>Protocolo</th>
        <th class="text-right">Guias</th>
        <th class="text-right">Faturado</th>
        <th class="text-right">Glosado</th>
        <th class="text-right hide-sm">Liberado</th>
        <th class="text-right hide-sm">Recebido</th>
        <th>Status</th>
        <th></th>
      </tr>
      </thead>
      <tbody>
      <tr v-for="item in items" :key="item.id" class="clickable"
          :class="{'is-canceled': item.status === 'canceled', 'is-new': newIds.includes(item.id)}"
          @click="$emit('detail', item)">
        <td><span class="text-bold num">{{ item.number }}</span></td>
        <td>
          <span class="cr-type" :class="`cr-type-${item.guideType}`">
            {{ guideTypeName(item.guideType) }}
          </span>
        </td>
        <td class="num">{{ item.dates.closedAt | date }}</td>
        <td class="hide-sm num" :class="{'text-gray': !item.dates.sentAt}">
          {{ item.dates.sentAt ? dateFilter(item.dates.sentAt) : '—' }}
        </td>
        <td>
          <template v-if="item.protocol && item.protocol.number">
            <span class="num">{{ item.protocol.number }}</span>
            <span class="sub" v-if="item.protocol.date">{{ item.protocol.date | date }}</span>
          </template>
          <template v-else-if="item.status === 'canceled'">
            <span class="text-gray">—</span>
            <span class="sub">Cancelado em {{ item.dates.canceledAt | date }}</span>
          </template>
          <template v-else>
            <span class="text-gray">—</span>
            <span class="sub" v-if="item.file && item.file.generatedAt">
              XML gerado em {{ item.file.generatedAt | date }}
            </span>
          </template>
        </td>
        <td class="text-right num">{{ item.quantity }}</td>
        <td class="text-right num text-bold" :class="{strike: item.status === 'canceled'}">
          {{ item.totals.informed | currency }}
        </td>
        <td class="text-right num" :class="glossedClass(item)">{{ money(item, 'glossed') }}</td>
        <td class="text-right num hide-sm" :class="{'text-gray': !analyzed(item)}">
          {{ money(item, 'released') }}
        </td>
        <td class="text-right num hide-sm" :class="paidClass(item)">{{ money(item, 'paid') }}</td>
        <td><batch-status-badge :status="item.status"/></td>
        <td class="actions-cell" @click.stop>
          <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                  data-tooltip="Detalhes" @click="$emit('detail', item)">
            <fa-icon :icon="['fal', 'eye']"/>
          </button>
          <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                  v-if="tissEnabled"
                  :data-tooltip="xmlTooltip(item)"
                  :class="{loading: busyId === item.id && busyAction === 'xml'}"
                  :disabled="!!xmlDisabled(item) || busyId === item.id"
                  @click="$emit('xml', item)">
            <fa-icon :icon="['fal', 'cloud-download']"/>
          </button>
          <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                  :data-tooltip="coverDisabled(item) || 'Capa do lote'"
                  :disabled="!!coverDisabled(item)"
                  @click="$emit('cover', item)">
            <fa-icon :icon="['fal', 'print']"/>
          </button>
          <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                  :data-tooltip="sendDisabled(item) || 'Marcar como enviado'"
                  :disabled="!!sendDisabled(item)"
                  @click="$emit('send', item)">
            <fa-icon :icon="['fal', 'paper-plane']"/>
          </button>
          <button class="btn btn-sm btn-action btn-icon btn-gray tooltip tooltip-left"
                  :data-tooltip="importDisabled(item) || 'Importar demonstrativo'"
                  :disabled="!!importDisabled(item)"
                  @click="$emit('import', item)">
            <fa-icon :icon="['fal', 'file-import']"/>
          </button>
        </td>
      </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import currency from '@/filters/currency';
import dateFilter from '@/filters/date';
import { guideTypeName } from '../service';
import BatchStatusBadge from './BatchStatusBadge.vue';

const ANALYZED = ['analyzed', 'in_appeal', 'settled'];

export default {
  components: {
    BatchStatusBadge,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    tissEnabled: {
      type: Boolean,
      default: true,
    },
    canUpdate: {
      type: Boolean,
      default: true,
    },
    canPrint: {
      type: Boolean,
      default: true,
    },
    canXml: {
      type: Boolean,
      default: true,
    },
    canImport: {
      type: Boolean,
      default: true,
    },
    newIds: {
      type: Array,
      default: () => [],
    },
    busyId: {
      type: String,
      default: null,
    },
    busyAction: {
      type: String,
      default: null,
    },
  },
  methods: {
    guideTypeName,
    dateFilter,
    analyzed(item) {
      return ANALYZED.includes(item.status);
    },
    money(item, field) {
      if (item.status === 'canceled') return '—';
      if (field === 'paid' && item.status !== 'settled' && !(item.totals.paid > 0)) return '—';
      if (field !== 'paid' && !this.analyzed(item)) return '—';
      return currency(item.totals[field] || 0);
    },
    glossedClass(item) {
      if (!this.analyzed(item) || item.status === 'canceled') return 'text-gray';
      return item.totals.glossed > 0 ? 'text-error' : '';
    },
    paidClass(item) {
      if (item.status === 'canceled' || !(item.totals.paid > 0)) return 'text-gray';
      return item.totals.paid >= item.totals.released ? 'text-success text-bold' : '';
    },
    xmlDisabled(item) {
      if (item.status === 'canceled') return 'Lote cancelado';
      if (!this.canXml) return 'Sem permissão';
      return null;
    },
    xmlTooltip(item) {
      return this.xmlDisabled(item) || (item.status === 'closed' ? 'Gerar XML TISS' : 'Baixar XML');
    },
    coverDisabled(item) {
      if (item.status === 'canceled') return 'Lote cancelado';
      if (!this.canPrint) return 'Sem permissão';
      return null;
    },
    sendDisabled(item) {
      if (item.status === 'canceled') return 'Lote cancelado';
      if (item.status === 'sent') return `Já enviado em ${dateFilter(item.dates.sentAt)}`;
      if (item.status !== 'closed') return 'Lote já enviado';
      if (!this.canUpdate) return 'Sem permissão';
      return null;
    },
    importDisabled(item) {
      if (item.status === 'canceled') return 'Lote cancelado';
      if (item.status === 'closed') return 'Envie o lote antes';
      if (item.status === 'settled') return 'Lote baixado';
      if (!this.canImport) return 'Sem permissão';
      return null;
    },
  },
};
</script>
